import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';

import ValidationError from '../ValidationError/ValidationError';
import HelpText from '../HelpText/HelpText';
import { formatRequiredLabel } from '../../util/uiHelpers';
import {
  getOptionControlMode,
  OPTION_CONTROL_SEGMENTED,
  CHIP_LIMIT,
} from '../../config/listingOptionControls';

import css from './FieldOptionGroup.module.css';

const SingleOptionGroup = props => {
  const {
    id,
    name,
    label,
    helpText,
    options = [],
    isRequired,
    className,
    rootClassName,
    layout,
    validate,
    onChange,
    chipLimit = CHIP_LIMIT,
  } = props;

  const mode = getOptionControlMode(options.length, chipLimit);
  const isSegmented = layout === 'segmented' || (!layout && mode === OPTION_CONTROL_SEGMENTED);
  const isGrid = layout === 'grid2x2';
  const listClass = isGrid
    ? css.grid2x2
    : classNames(css.options, isSegmented ? css.segmented : css.chips);
  const displayLabel = formatRequiredLabel(label, isRequired);
  const classes = classNames(rootClassName || css.root, className);

  return (
    <Field
      name={name}
      validate={validate}
      subscription={{ value: true, touched: true, error: true }}
    >
      {({ input, meta }) => (
        <fieldset className={classes}>
          {displayLabel ? <legend className={css.label}>{displayLabel}</legend> : null}
          <ul
            className={listClass}
            role="radiogroup"
            aria-label={typeof label === 'string' ? label : undefined}
          >
            {options.map(option => {
              const optionId = `${id}.${option.key}`;
              const checked = input.value === option.key;
              const selectOption = () => {
                input.onChange(option.key);
                if (onChange) {
                  onChange(option.key);
                }
              };
              // Native radios can't clear once selected. For optional fields, a second
              // click on the active option clears the value (onChange does not fire).
              const clearIfOptionalReselect = () => {
                if (!isRequired && checked) {
                  input.onChange(null);
                  if (onChange) {
                    onChange(null);
                  }
                }
              };
              return (
                <li key={option.key} className={css.item}>
                  <input
                    id={optionId}
                    className={css.input}
                    type="radio"
                    name={input.name}
                    value={option.key}
                    checked={checked}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    onClick={clearIfOptionalReselect}
                    onChange={selectOption}
                  />
                  <label htmlFor={optionId} className={css.option}>
                    {option.label}
                  </label>
                </li>
              );
            })}
          </ul>
          <HelpText helpText={helpText} />
          <ValidationError fieldMeta={meta} />
        </fieldset>
      )}
    </Field>
  );
};

const MultiOptionGroup = props => {
  const {
    id,
    name,
    label,
    helpText,
    options = [],
    isRequired,
    className,
    rootClassName,
    validate,
  } = props;
  const form = useForm();

  const displayLabel = formatRequiredLabel(label, isRequired);
  const classes = classNames(rootClassName || css.root, className);

  return (
    <FieldArray name={name} validate={validate}>
      {fieldArrayProps => {
        const { fields, meta } = fieldArrayProps;
        const selected = fields.value || [];

        const toggle = key => {
          const index = selected.indexOf(key);
          if (index >= 0) {
            fields.remove(index);
          } else {
            fields.push(key);
          }
        };

        return (
          <fieldset className={classes}>
            {displayLabel ? <legend className={css.label}>{displayLabel}</legend> : null}
            <ul className={classNames(css.options, css.chips)}>
              {options.map(option => {
                const optionId = `${id}.${option.key}`;
                const checked = selected.includes(option.key);
                return (
                  <li key={option.key} className={css.item}>
                    <input
                      id={optionId}
                      className={css.input}
                      type="checkbox"
                      name={name}
                      checked={checked}
                      onFocus={() => form.focus(name)}
                      onBlur={() => form.blur(name)}
                      onChange={() => toggle(option.key)}
                    />
                    <label htmlFor={optionId} className={css.option}>
                      {checked ? (
                        <span className={css.mark} aria-hidden="true">
                          ✓
                        </span>
                      ) : null}
                      {option.label}
                    </label>
                  </li>
                );
              })}
            </ul>
            <HelpText helpText={helpText} />
            <ValidationError fieldMeta={meta} />
          </fieldset>
        );
      }}
    </FieldArray>
  );
};

/**
 * Final Form field: native radio/checkbox options styled as segmented row or chips.
 *
 * @component
 */
const FieldOptionGroup = props => {
  const { multiple = false, options = [], chipLimit = CHIP_LIMIT, ...rest } = props;

  if (!options.length) {
    return null;
  }

  if (multiple) {
    return <MultiOptionGroup {...rest} options={options} />;
  }

  return <SingleOptionGroup {...rest} options={options} chipLimit={chipLimit} />;
};

export default FieldOptionGroup;
